import React, { useEffect, useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import API from "../../../../utils/API";
import MesTable from "../../../../components/Table/MesTable";
import stopFilled from "../../../../assets/icons/message-manager/stopFilled.svg";
import constants from "../../../../utils/constants";
import commonMethods from "../../../../utils/common-methods";
import { useIntl, FormattedMessage } from "react-intl";
import useInfiniteScroll from "../../../../components/common/useInfiniteScroll";
import dustbin from "../../../../assets/icons/message-manager/dustbin.svg";
import { trackEvent } from "../../../../utils/analytics";

const SIGNAGE_TEXT_PREFIX = "SIGNAGE_";

function SignageStatusTable(props) {
  const {
    updateFlag,
    setUpdateFlag,
    onLoadStatusTable,
    setNotificationProps,
    modalRef,
  } = props;
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState("sent");
  const [sortByAsc, setSortByAsc] = useState(false);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [statusData, setStatusData] = useState([]);

  const tableRef = useRef();
  const tableWrapperRef = useRef();
  const hasTrackedRef = useRef(false);
  const intl = useIntl();

  const loadStatusTable = async (map, resetSelection = true) => {
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    try {
      const res = await API.getAllSignageStatus();
      res.data.forEach((mess) => {
        const scheduled = commonMethods.formatScheduler(mess.scheduler);
        map.data[mess.id] = {
          id: mess.id,
          title: mess.title,
          scheduled,
          scheduledId: mess.scheduler?.id,
          sent: commonMethods.formatDate(mess.sendAt),
          groups: Array.isArray(mess.groups)
            ? mess.groups.map((o) => o.name).join(", ")
            : mess.groups?.name || "",
          status: intl.formatMessage({
            id: SIGNAGE_TEXT_PREFIX + mess.status,
            defaultMessage: mess.status,
          }),
          checked: false,
          createdBy: `${mess.createdBy?.firstName || ""} ${mess.createdBy?.lastName || ""}`,
        };
      });
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setStatusData(res.data);
      setDataCount(map.ids.length);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);

      // Track dashboard view once on initial load
      if (!hasTrackedRef.current && res.data.length > 0) {
        hasTrackedRef.current = true;
        const scheduleTypes = { daily: 0, weekly: 0 };
        let totalDurationHours = 0;

        res.data.forEach((item) => {
          if (item.scheduler?.type === "daily") scheduleTypes.daily++;
          else scheduleTypes.weekly++;

          if (item.scheduler?.timeStart && item.scheduler?.timeEnd) {
            const start = new Date(item.scheduler.timeStart);
            const end = new Date(item.scheduler.timeEnd);
            const durationHours = (end - start) / (1000 * 60 * 60);
            if (durationHours > 0) totalDurationHours += durationHours;
          }
        });

        trackEvent(
          "signage.dashboard_owner_status",
          {
            running_signage_count: res.data.length,
            schedule_type_daily_count: scheduleTypes.daily,
            schedule_type_weekly_count: scheduleTypes.weekly,
            avg_duration_hours:
              res.data.length > 0
                ? Math.round(totalDurationHours / res.data.length)
                : 0,
          },
          props.user,
        );
      }
    } catch (err) {
      console.log("err", err);
      // If it's error 502, skip error handling
      if (err.response && err.response.status === 502) return;

      // Safe error message handling
      const errorMessage = err.response?.data?.message || "GENERAL_ERROR";
      setError(
        <FormattedMessage
          id={errorMessage}
          defaultMessage="An error occurred"
        />,
      );
      setDataLoaded(true);
    }
  };

  useEffect(() => {
    if (onLoadStatusTable) {
      onLoadStatusTable(() => loadStatusTable(dataMap));
    }
  }, [onLoadStatusTable]);

  const dataMap = useMemo(() => {
    const map = {
      data: {},
      ids: [],
      selectedIds: {},
      filteredIds: [],
      sortedIds: [],
    };
    loadStatusTable(map);
    return map;
  }, []);

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    rowHeight: 48,
  });

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== "") {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, statusData]);

  useEffect(() => {
    loadStatusTable(dataMap);
  }, [updateFlag]);

  const handleSingleDelete = async (e, id, scheduledId) => {
    e.preventDefault();
    try {
      const res = await API.stopSignage(id, scheduledId);
      if (res && res.status === 200) {
        setSuccess(<FormattedMessage id="SIGNAGE_STOPPED_SUCCESSFULLY" />);
      }
      modalRef.current.hide();
      setUpdateFlag((prev) => !prev);
    } catch (error) {
      setError(
        <FormattedMessage
          id="SIGNAGE_STOPPED_FAILED"
          values={{ errorMsg: error.message }}
        />,
      );
      modalRef.current.hide();
    }
  };

  const tableColumnArray = [
    {
      Header: "SIGNAGE",
      id: "title",
      accessor: "title",
      isVisible: true,
    },
    {
      Header: "SCHEDULED",
      id: "scheduled",
      accessor: "scheduled",
      isVisible: true,
    },
    {
      Header: "GROUPS",
      id: "groups",
      isVisible: true,
      accessor: "groups",
    },
    {
      Header: "CREATED_BY",
      id: "createdBy",
      isVisible: true,
      accessor: "createdBy",
    },
    {
      Header: "SENT",
      id: "sent",
      isVisible: true,
      accessor: "sent",
    },
    {
      Header: "STATUS",
      id: "status",
      isVisible: true,
      accessor: "status",
    },
    {
      Header: "",
      id: "actions",
      isVisible: true,
      mWidth: "mWidth-60",
      Cell: ({ row }) => (
        <div className="actions d-flex align-items-center">
          <button
            className="btn"
            id="stopMessage"
            data-bs-toggle="modal"
            data-bs-target="#notificationModal"
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              setNotificationProps({
                icon: dustbin,
                message: "STOP_SIGNAGE",
                description: "STOP_SIGNAGE_DESCRIPTION",
                showSecondaryButton: true,
                className: "green",
                primaryButtonText: "STOP",
                onSubmit: (e) =>
                  handleSingleDelete(
                    e,
                    row.original.id,
                    row.original.scheduledId,
                  ),
              });
            }}
          >
            <img
              src={stopFilled}
              className="deleteAction"
              alt={<FormattedMessage id="DELETE" />}
            />
          </button>
        </div>
      ),
    },
  ];

  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "sbds_owner_task_status_table",
    pageType: constants.pageType.MessageStatus,
    modalId: "SignageStatus",
    columns: tableColumnArray,
    data,
    setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount,
    setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success,
    setSuccess,
    error,
    setError,
    startIndex: startIndexForTable,
    selectedIds,
    setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    initialContainerHeight: "288px",
  };

  return (
    <div>
      <MesTable {...tableProps} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(SignageStatusTable);
